/**
 * Tours específicos para la sección de Vehículos
 *
 * Estructura de rutas:
 *  /vehicles              → DashboardVehicle              (lista de vehículos)
 *  /vehicles/:id          → ProfileVehicleAdministration  (perfil detallado)
 *  /vehicles/add/vehicle  → AddVehicleFlow                (agregar vehículo)
 *  /vehicles/setting/data → SettingVehicle                (configuración)
 */

// ---------------------------------------------------------------------------
// 1. LAYOUT COMPARTIDO — header de Vehicles.jsx
// ---------------------------------------------------------------------------
export const getVehiclesLayoutTour = (theme) => [
  {
    element: `.GM__${theme}-vehicles-title`,
    popover: {
      title: 'Título de la sección',
      description:
        'Indica en qué sección de Vehículos te encuentras. Cambia automáticamente según la ruta que estés visitando.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-vehicles-navigation`,
    popover: {
      title: 'Ruta de navegación (Breadcrumb)',
      description:
        'Muestra la ruta completa desde la raíz hasta la página actual. Haz clic en los segmentos anteriores para volver atrás sin perder contexto.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-vehicles-filter-actions-desktop`,
    popover: {
      title: 'Acciones y filtros',
      description:
        'Zona de filtros específicos de la vista actual más las acciones rápidas globales: configuración y agregar nuevo vehículo.',
      side: 'bottom',
      align: 'end',
    },
  },
  {
    element: `.GM__${theme}-btn-settings`,
    popover: {
      title: 'Configuración de vehículos',
      description:
        'Accede a la configuración global: tipos de vehículos, categorías, documentos requeridos y demás catálogos necesarios para crear o gestionar vehículos.',
      side: 'bottom',
      align: 'end',
    },
  },
  {
    element: `.GM__${theme}-btn-add-vehicle`,
    popover: {
      title: 'Agregar nuevo vehículo',
      description:
        'Inicia el flujo de registro para incorporar un nuevo vehículo a la flota. Se abrirá un formulario guiado paso a paso.',
      side: 'bottom',
      align: 'end',
    },
  },
];

// ---------------------------------------------------------------------------
// 2. DASHBOARD DE VEHÍCULOS — /vehicles
// ---------------------------------------------------------------------------
export const getVehiclesDashboardTour = (theme) => [
  ...getVehiclesLayoutTour(theme),
  {
    element: `.GM__${theme}-vehicles-filter-actions`,
    popover: {
      title: 'Filtros de búsqueda y orden',
      description:
        'Busca por matrícula o tipo, ordena la lista y alterna entre vehículos activos/inactivos y asociaciones activas/inactivas.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-vehicle-tabs-container`,
    popover: {
      title: 'Filtro por tipo de vehículo',
      description:
        'La pestaña "Todos" muestra la flota completa; las demás filtran por categoría o tipo específico. Usa las flechas si hay más pestañas de las que caben en pantalla.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-vehicle-tab.active`,
    popover: {
      title: 'Pestaña activa',
      description:
        'La pestaña resaltada indica el tipo visualizado actualmente. Haz clic en cualquier otra para cambiar el filtro al instante.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-dashboard-vehicle`,
    popover: {
      title: 'Listado de vehículos',
      description:
        'Tarjetas de todos los vehículos según los filtros activos. Cada tarjeta muestra matrícula, tipo, conductor asignado y estado actual. Haz clic en una tarjeta para ver el perfil completo.',
      side: 'top',
      align: 'start',
    },
  },
];

// ---------------------------------------------------------------------------
// 3. PERFIL DEL VEHÍCULO — /vehicles/:id
//    Factory con interacción: auto-expand, pregunta edit mode y docs detallados
// ---------------------------------------------------------------------------

/**
 * @param {string}  theme        — tema actual ("dark" | "light")
 * @param {object}  driverRef    — ref.current = instancia driver.js
 * @param {boolean} canEdit      — ¿el usuario puede editar el vehículo?
 */
export const createVehicleProfileTour = (theme, driverRef, canEdit = true) => {
  const getDriver = () => driverRef.current;

  // Utilidad: expand la sección de información si no está abierta
  const ensureProfileExpanded = () => {
    const section = document.querySelector(`.VPA__${theme}-user-section`);
    if (section && !section.classList.contains('expanded')) {
      const btn = section.querySelector(`.VPA__${theme}-user-expand-button`);
      btn?.click();
    }
  };

  // Utilidad: colapsa la sección de información si está abierta
  const ensureProfileCollapsed = () => {
    const section = document.querySelector(`.VPA__${theme}-user-section`);
    if (section && section.classList.contains('expanded')) {
      const btn = section.querySelector(`.VPA__${theme}-user-expand-button`);
      btn?.click();
    }
  };

  // ── A. Pasos de layout ──────────────────────────────────────────────────
  const layoutSteps = getVehiclesLayoutTour(theme);

  // ── B. Pasos de información EN MODO LECTURA ─────────────────────────────
  const infoReadSteps = [
    {
      // Contexto general de la vista
      element: `.VPA__${theme}-main-container`,
      popover: {
        title: 'Perfil completo del vehículo',
        description:
          'La vista se divide en dos grandes zonas: arriba los datos del vehículo y abajo la gestión documental. Recorreremos ambas detalladamente.',
        side: 'top',
        align: 'start',
      },
    },
    {
      // Sección info — se expande automáticamente al llegar aquí
      element: `.VPA__${theme}-user-section`,
      onHighlightStarted: () => ensureProfileExpanded(),
      popover: {
        title: 'Información del vehículo — abriendo sección',
        description:
          'Esta sección contiene todos los datos del vehículo. Se está expandiendo automáticamente para mostrarte el detalle completo.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      // Botón expandir/contraer
      element: `.VPA__${theme}-user-expand-button`,
      popover: {
        title: 'Expandir / Contraer',
        description:
          'Permite ampliar o reducir esta sección. Cuando está contraída, solo ves los datos esenciales. Expandida, muestra todos los campos: técnicos, contrato, propietario, operador y GPS.',
        side: 'right',
        align: 'center',
      },
    },
    {
      // Fotos del vehículo
      element: `.VIP__${theme}-photos-section`,
      popover: {
        title: 'Foto del vehículo',
        description:
          'Imagen principal del vehículo. Si está asociado a otro (p. ej. cabezote + remolque), aparecerán dos fotos. Haz clic en la secundaria para alternar la vista de datos entre ambos.',
        side: 'right',
        align: 'start',
      },
    },
    {
      // Placa e ID
      element: `.VIP__${theme}-identity-row`,
      popover: {
        title: 'Placa e identificador interno',
        description:
          'La placa identifica al vehículo ante la autoridad y en el sistema. El ID interno es único e irrepetible en la base de datos, usado para referencias cruzadas con viajes, mantenimientos y documentos.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      // Chips de atributos
      element: `.VIP__${theme}-chips-row`,
      popover: {
        title: 'Atributos clave (modo lectura)',
        description:
          'Vista rápida de: tipo de vehículo, marca, modelo, operación asignada y estado activo/inactivo. En modo lectura son solo indicativos; en modo edición se convierten en selectores interactivos.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      // Área expandida completa
      element: `.VIP__${theme}-user-expanded-content`,
      popover: {
        title: 'Datos detallados del vehículo',
        description:
          'Sección expandida con toda la información distribuida en dos columnas. Columna izquierda: datos técnicos y contrato. Columna derecha: teléfonos, propietario, operador y configuración GPS.',
        side: 'top',
        align: 'start',
      },
    },
    {
      // Columna izquierda
      element: `.VIP__${theme}-column-left`,
      popover: {
        title: 'Datos técnicos y contrato',
        description:
          'Tipo y categoría del vehículo, marca, modelo, año de matrícula, color, capacidad de carga, peso vacío, volumen, número de ejes, tipo de carga y nacionalidad. Más abajo, las fechas de inicio y fin del contrato vigente.',
        side: 'right',
        align: 'start',
      },
    },
    {
      // Columna derecha
      element: `.VIP__${theme}-column-right`,
      popover: {
        title: 'Propietario, operador y GPS',
        description:
          'Teléfonos asociados al vehículo (con indicativo de país), datos completos del propietario, datos del operador asignado y configuración del proveedor GPS: ID de dispositivo y credenciales de acceso.',
        side: 'left',
        align: 'start',
      },
    },
  ];

  // ── C. Pasos de modo EDICIÓN ────────────────────────────────────────────
  const editModeSteps = canEdit
    ? [
        {
          // Re-expand en modo edición
          element: `.VPA__${theme}-user-section`,
          onHighlightStarted: () => ensureProfileExpanded(),
          popover: {
            title: 'Modo edición — activo',
            description:
              'El perfil ahora está en modo edición. Los campos son interactivos. La sección se expande para que puedas ver y modificar cada dato.',
            side: 'bottom',
            align: 'start',
          },
        },
        {
          // Editar: placa y datos básicos inline
          element: `.VIP__${theme}-identity-row`,
          popover: {
            title: 'Editar: Placa y datos básicos visibles',
            description:
              'La placa del vehículo es editable directamente. Según el tipo de vehículo, también aparecen inline los campos de modelo, modelo del tanque y color. Modifícalos con precaución, ya que la placa debe ser única.',
            side: 'bottom',
            align: 'start',
          },
        },
        {
          // Editar: chips → selectores
          element: `.VIP__${theme}-chips-row`,
          popover: {
            title: 'Editar: Tipo, marca, operación y estado',
            description:
              'Los chips se convierten en selectores desplegables. Puedes cambiar el tipo de vehículo, la marca, la operación asignada y la operación logística. El interruptor al final activa o desactiva el vehículo en el sistema.',
            side: 'bottom',
            align: 'start',
          },
        },
        {
          // Editar: contenido expandido completo
          element: `.VIP__${theme}-user-expanded-content`,
          popover: {
            title: 'Editar: Datos completos',
            description:
              'Todos los campos del vehículo ahora están disponibles para su edición. Recorreremos cada grupo a continuación.',
            side: 'top',
            align: 'start',
          },
        },
        {
          // Editar: columna izquierda — datos técnicos
          element: `.VIP__${theme}-column-left`,
          popover: {
            title: 'Editar: Datos técnicos del vehículo',
            description:
              'Campos editables: año/fecha de matrícula, capacidad de carga, peso vacío, volumen, número de ejes y tipo de carga. Estos datos afectan directamente la asignación operativa del vehículo.',
            side: 'right',
            align: 'start',
          },
        },
        {
          // Editar: columna derecha — teléfonos, propietario, operador, GPS
          element: `.VIP__${theme}-column-right`,
          popover: {
            title: 'Editar: Teléfonos, propietario, operador y GPS',
            description:
              'Puedes cambiar los teléfonos del vehículo (con código de país), seleccionar un nuevo propietario, reasignar el operador y actualizar las credenciales del proveedor GPS (ID de dispositivo y contraseña).',
            side: 'left',
            align: 'start',
          },
        },
        {
          // Editar: gestión de trailer — colapsa la vista expandida al llegar aquí
          element: `.VIPE__${theme}-trailer-management-section`,
          onHighlightStarted: () => ensureProfileCollapsed(),
          popover: {
            title: 'Gestión de remolque / tráiler',
            description:
              'Si el vehículo puede llevar remolque, aquí puedes asociar uno escribiendo su placa o desasociar el actual. El sistema valida si el remolque elegido ya está asignado a otro cabezote antes de confirmar.',
            side: 'top',
            align: 'start',
          },
        },
        {
          // Editar: botones guardar / cancelar en el header
          element: `.VPA__${theme}-filter-actions-desktop`,
          popover: {
            title: 'Guardar o cancelar cambios',
            description:
              'Cuando termines de editar, haz clic en "Guardar" (ícono de disco) para persistir todos los cambios. Usa "Cancelar" (ícono X) para descartar todo y volver al modo lectura sin guardar nada.',
            side: 'bottom',
            align: 'end',
          },
        },
      ]
    : [];

  // ── D. Pasos de DOCUMENTOS ──────────────────────────────────────────────
  const docsSteps = [
    {
      // Filtros de documentos — colapsa la info del vehículo al llegar aquí (desde flujo edición)
      element: `.VPA__${theme}-filter-search`,
      onHighlightStarted: () => ensureProfileCollapsed(),
      popover: {
        title: 'Filtrar documentos',
        description:
          'Escribe el nombre o tipo de documento para encontrarlo rápidamente entre todas las secciones. La búsqueda filtra en tiempo real tanto los documentos subidos como los requeridos pendientes.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      // Acciones de documentos (header)
      element: `.VPA__${theme}-filter-actions-desktop`,
      popover: {
        title: 'Acciones sobre documentos',
        description: canEdit
          ? 'Botones disponibles: filtrar documentos excedentes, descargar múltiples archivos, ver la papelera de documentos eliminados y abrir el panel de comentarios.'
          : 'Puedes descargar múltiples documentos a la vez. La edición de documentos no está habilitada para tu rol actual.',
        side: 'bottom',
        align: 'end',
      },
    },
    {
      // Contenedor general de documentos
      element: `.VPA__${theme}-documents-container`,
      popover: {
        title: 'Zona de documentos',
        description:
          'Todas las secciones documentales del vehículo aparecen aquí. Se dividen en: Documentos Obligatorios (requeridos por normativa), Documentos No Obligatorios y, si aplica, documentos del remolque asociado.',
        side: 'top',
        align: 'start',
      },
    },
    {
      // Una sección de documentos
      element: `.VPA__${theme}-document-section`,
      popover: {
        title: 'Sección de documentos',
        description:
          'Cada sección tiene un botón de expandir/contraer independiente. Expandida, muestra la tabla completa con todos los documentos de esa categoría y sus acciones.',
        side: 'top',
        align: 'start',
      },
    },
    {
      // Tabla de documentos
      element: `.VDS__${theme}-table`,
      popover: {
        title: 'Tabla de documentos',
        description:
          'Columnas: Estado (indicador de color), Nombre del documento, Fecha de vencimiento y Acciones. Los documentos que aún no se han subido aparecen como "pendientes de carga".',
        side: 'top',
        align: 'start',
      },
    },
    {
      // Una fila de documento
      element: `.VDS__${theme}-table-row`,
      popover: {
        title: 'Fila de documento',
        description:
          'Cada fila es un tipo de documento requerido. El indicador de estado usa colores: 🟢 Vigente, 🟡 Próximo a vencer (menos de 30 días), 🔴 Vencido. Si no hay archivo cargado, aparece como pendiente.',
        side: 'top',
        align: 'start',
      },
    },
    {
      // Acciones de una fila
      element: `.VDS__${theme}-actions-container`,
      popover: {
        title: 'Acciones del documento',
        description: canEdit
          ? '👁 Previsualizar el archivo sin descargarlo. ⬆ Subir un archivo nuevo o actualizar el existente arrastrándolo o seleccionándolo. 🗑 Eliminar el documento (irá a la papelera y podrá recuperarse).'
          : '👁 Previsualizar el archivo del documento. La edición y carga de documentos no está disponible para tu rol actual.',
        side: 'left',
        align: 'center',
      },
    },
  ];

  // ── Calcular índices para saltos del step de decisión ───────────────────
  const editStartIdx =
    layoutSteps.length + infoReadSteps.length + 1; // +1 por el step de decisión
  const docsStartIdx = editStartIdx + editModeSteps.length;

  // ── E. Paso de DECISIÓN (¿explorar edición?) ────────────────────────────
  const decisionStep = canEdit
    ? [
        {
          element: `.VPA__${theme}-user-section`,
          onHighlighted: () => {
            // Inyectar handlers en los botones custom del HTML del popover
            setTimeout(() => {
              document
                .getElementById('tour-btn-yes-edit')
                ?.addEventListener('click', () => {
                  const editBtn = document.querySelector(
                    '[data-label="Editar Perfil"]'
                  );
                  if (editBtn) {
                    editBtn.click();
                    // Dar tiempo a React para re-renderizar en modo edición
                    setTimeout(
                      () => getDriver()?.moveTo(editStartIdx),
                      600
                    );
                  } else {
                    // El botón no existe (rol sin permisos de edición en DOM)
                    getDriver()?.moveTo(docsStartIdx);
                  }
                });

              document
                .getElementById('tour-btn-no-edit')
                ?.addEventListener('click', () => {
                  ensureProfileCollapsed();
                  getDriver()?.moveTo(docsStartIdx);
                });
            }, 50);
          },
          popover: {
            title: '¿Explorar el modo de edición?',
            description: `<p style="margin:0 0 12px 0;line-height:1.55">Has visto todos los datos en modo lectura. ¿Deseas explorar cómo funciona la edición de cada campo del vehículo?</p><div style="display:flex;gap:8px"><button id="tour-btn-yes-edit" style="padding:7px 18px;background:#3b82f6;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:13px;font-weight:600">Sí, explorar edición</button><button id="tour-btn-no-edit" style="padding:7px 18px;background:transparent;border:1px solid #9ca3af;border-radius:6px;cursor:pointer;font-size:13px;color:inherit">Ir a documentos</button></div>`,
            showButtons: ['close'],
            side: 'bottom',
            align: 'center',
          },
        },
      ]
    : [];

  // ── Componer el tour completo ────────────────────────────────────────────
  return [
    ...layoutSteps,
    ...infoReadSteps,
    ...decisionStep,
    ...editModeSteps,
    ...docsSteps,
  ];
};

// ---------------------------------------------------------------------------
// 4. AGREGAR VEHÍCULO — /vehicles/add/vehicle
// ---------------------------------------------------------------------------
export const getVehicleAddTour = (theme) => [
  ...getVehiclesLayoutTour(theme),
  {
    element: `.GM__${theme}-add-vehicle-container`,
    popover: {
      title: 'Formulario de nuevo vehículo',
      description:
        'Aquí registras un vehículo nuevo. El proceso está dividido en pasos guiados para facilitar el ingreso de toda la información requerida.',
      side: 'top',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-stepper`,
    popover: {
      title: 'Pasos del registro',
      description:
        'Indica el progreso dentro del formulario. Puedes navegar hacia adelante o atrás. Los pasos completados quedan marcados con un ícono de check.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-step-content`,
    popover: {
      title: 'Contenido del paso actual',
      description:
        'Los campos del paso en curso. Completa todos los campos requeridos antes de avanzar al siguiente paso.',
      side: 'top',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-navigation-buttons`,
    popover: {
      title: 'Navegación entre pasos',
      description:
        '"Atrás" para corregir el paso anterior. "Siguiente" para avanzar. En el último paso aparece el botón "Guardar" para registrar el vehículo.',
      side: 'top',
      align: 'end',
    },
  },
];

// ---------------------------------------------------------------------------
// 5. CONFIGURACIÓN DE VEHÍCULOS — /vehicles/setting/data
// ---------------------------------------------------------------------------
export const getVehicleSettingTour = (theme) => [
  ...getVehiclesLayoutTour(theme),
  {
    element: `.GM__${theme}-dashboard-vehicle`,
    popover: {
      title: 'Panel de configuración',
      description:
        'Catálogos maestros para la gestión vehicular: tipos de vehículos, categorías de documentos, propietarios y demás datos requeridos.',
      side: 'top',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-settings-tabs`,
    popover: {
      title: 'Categorías de configuración',
      description:
        'Cada pestaña corresponde a un catálogo configurable. Navega entre ellas para gestionar tipos de vehículos, documentos requeridos u otras opciones.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-tab-navigation-container`,
    popover: {
      title: 'Navegación de pestañas',
      description:
        'Usa las flechas laterales o el indicador de páginas para desplazarte cuando hay más pestañas de las que caben en pantalla.',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    element: `.GM__${theme}-tab-content`,
    popover: {
      title: 'Contenido de la configuración',
      description:
        'Aquí creas, editas o eliminas los registros del catálogo seleccionado. Los cambios afectan directamente las opciones disponibles al registrar vehículos.',
      side: 'top',
      align: 'start',
    },
  },
];

// ---------------------------------------------------------------------------
// SELECTOR — elige el tour según la ruta actual
// (Para /vehicles/:id usar createVehicleProfileTour directamente desde TourContext)
// ---------------------------------------------------------------------------
export const getVehicleTourByPath = (theme, pathname) => {
  if (pathname.includes('/vehicles/add/vehicle')) {
    return getVehicleAddTour(theme);
  }
  if (pathname.includes('/vehicles/setting/data')) {
    return getVehicleSettingTour(theme);
  }
  return getVehiclesDashboardTour(theme);
};
